import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AccountBillingConfigDto, BillingConfigRequest } from '../../models/billing-config.model';

/**
 * Admin client for an account's billing configuration and first-period generation.
 */
@Injectable({ providedIn: 'root' })
export class AccountBillingService {
  private baseUrl = `${environment.apiUrl}/api/admin/accounts`;

  constructor(private http: HttpClient) {}

  get(accountId: number): Observable<AccountBillingConfigDto | null> {
    return this.http.get<AccountBillingConfigDto | null>(`${this.baseUrl}/${accountId}/billing-config`);
  }

  save(accountId: number, body: BillingConfigRequest): Observable<AccountBillingConfigDto> {
    return this.http.put<AccountBillingConfigDto>(`${this.baseUrl}/${accountId}/billing-config`, body);
  }

  generateFirstPeriod(accountId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${accountId}/billing-config/generate-first-period`, {});
  }

  /**
   * Backfill every return from the account's start period up to the current month
   * (or its end period). Returns { created, scope }.
   */
  generateAllPeriods(accountId: number): Observable<{ created: number; scope: any }> {
    return this.http.post<{ created: number; scope: any }>(
      `${environment.apiUrl}/api/admin/returns/generate`, {}, { params: { accountId } });
  }

  /** Active properties missing from existing returns for periods they cover. */
  missingPropertyLines(accountId: number): Observable<MissingPropertyLine[]> {
    return this.http.get<MissingPropertyLine[]>(`${this.baseUrl}/${accountId}/billing-config/missing-property-lines`);
  }

  /** Add missing properties to open returns; paid periods get a supplemental return. */
  attachProperties(accountId: number): Observable<{ updated: number }> {
    return this.http.post<{ updated: number }>(`${this.baseUrl}/${accountId}/billing-config/attach-properties`, {});
  }
}

/**
 * ADD = merged into the open return; SUPPLEMENTAL = period already paid, separate return;
 * BOUNDARY_CONFLICT = the return crosses a shared-property boundary, so it is never repaired automatically.
 * BEFORE_BILLING_START = the property rents before the billing start (period = its rental month); no return.
 */
export interface MissingPropertyLine {
  propertyId: number;
  address: string;
  period: string;
  returnId: number;
  action: 'ADD' | 'SUPPLEMENTAL' | 'BOUNDARY_CONFLICT' | 'BEFORE_BILLING_START';
}
